import { useState, useEffect } from 'react';
import { translations } from './translations';
import { i18n } from '../translate/i18n';

// === Country code → language code map (top countries by likely audience) ===
const COUNTRY_TO_LANG = {
  // Spanish
  PE: 'es', MX: 'es', ES: 'es', AR: 'es', CL: 'es', CO: 'es', EC: 'es',
  BO: 'es', PY: 'es', UY: 'es', VE: 'es', GT: 'es', HN: 'es', NI: 'es',
  CR: 'es', SV: 'es', DO: 'es', PR: 'es', CU: 'es', GQ: 'es',
  // English
  US: 'en', GB: 'en', CA: 'en', AU: 'en', NZ: 'en', IE: 'en', ZA: 'en',
  IN: 'en', SG: 'en', PH: 'en', NG: 'en', KE: 'en', GH: 'en',
  // French
  FR: 'fr', BE: 'fr', LU: 'fr', MC: 'fr', SN: 'fr', CI: 'fr', CM: 'fr',
  // Italian
  IT: 'it', SM: 'it', VA: 'it',
};

const normalizeLanguage = (lng) => {
  const raw = String(lng || '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (lower === 'pt' || lower.startsWith('pt-')) return 'pt-BR';
  if (lower.startsWith('es')) return 'es';
  if (lower.startsWith('en')) return 'en';
  if (lower.startsWith('fr')) return 'fr';
  if (lower.startsWith('it')) return 'it';
  return raw;
};

const getInitialLanguage = () => {
  const candidates = [
    localStorage.getItem('i18nextLng'),
    localStorage.getItem('language'),
    navigator.language,
  ];
  for (const cand of candidates) {
    const normalized = normalizeLanguage(cand);
    if (normalized && translations[normalized]) return normalized;
  }
  return 'es';
};

// === Detector por IP (cache 24h en localStorage) ===
const detectLanguageByIP = async () => {
  try {
    const cacheKey = 'klorum.iplang.v1';
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { lang, ts, country } = JSON.parse(cached);
      if (Date.now() - ts < 24 * 3600 * 1000 && lang && translations[lang]) {
        return { lang, country, source: 'cache' };
      }
    }
    const res = await fetch('https://ipapi.co/json/', { cache: 'no-store' });
    if (!res.ok) return null;
    const d = await res.json();
    const country = (d && d.country_code) ? String(d.country_code).toUpperCase() : '';
    const lang = COUNTRY_TO_LANG[country] || null;
    if (lang && translations[lang]) {
      localStorage.setItem(cacheKey, JSON.stringify({ lang, country, ts: Date.now() }));
      return { lang, country, source: 'ip' };
    }
  } catch (e) {
    // silent
  }
  return null;
};

export const useLanguage = () => {
  const [language, setLanguage] = useState(() => getInitialLanguage());
  const [suggestedByIP, setSuggestedByIP] = useState(null); // {lang, country}

  useEffect(() => {
    localStorage.setItem('language', language);
    localStorage.setItem('i18nextLng', language);
    i18n.changeLanguage(language).catch(() => undefined);
  }, [language]);

  // Lookup IP only on first mount + only if user never picked language manually
  useEffect(() => {
    const userPicked = localStorage.getItem('klorum.langPicked');
    if (userPicked === '1') return;
    let cancelled = false;
    detectLanguageByIP().then((r) => {
      if (cancelled || !r) return;
      setSuggestedByIP(r);
      // If different from current AND user never explicitly set: use IP detection on first visit
      const noLocalChoice = !localStorage.getItem('klorum.langPicked');
      if (noLocalChoice && r.lang !== language) {
        // we DO NOT auto-switch (banner will offer it). Just expose suggestion.
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = translations[language] || translations.es;

  // Helper called by banner / selector
  const selectLanguage = (lng) => {
    const norm = normalizeLanguage(lng);
    if (norm && translations[norm]) {
      localStorage.setItem('klorum.langPicked', '1');
      setLanguage(norm);
    }
  };

  return {
    language,
    setLanguage,
    selectLanguage,
    suggestedByIP, // {lang, country, source} | null
    t,
  };
};
